import axios, { AxiosResponse } from 'axios'

const buildApiUrl = (path: string): string => {
  const base = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '')
  return base ? `${base}/api/${path}` : `/api/${path}`
}

axios.defaults.withCredentials = true
axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN'

let latestXsrfToken: string | undefined

interface AntiforgeryTokenResponse {
  token: string
}

const buildAntiforgeryTokenUrl = (): string => {
  const base = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '')
  return base ? `${base}/api/antiforgery/token` : `/api/antiforgery/token`
}

const setLatestXsrfToken = (token?: string) => {
  if (!token) return latestXsrfToken
  latestXsrfToken = token
  return latestXsrfToken
}

export const clearAntiforgeryToken = () => {
  latestXsrfToken = undefined
}

const fetchAntiforgeryToken = async (): Promise<string | undefined> => {
  try {
    const res = await axios.get<AntiforgeryTokenResponse>(
      buildAntiforgeryTokenUrl(),
      { withCredentials: true }
    )

    return setLatestXsrfToken(res.data?.token)
  } catch (err) {
    // Surface the cause (CSP block, CORS, 401, network) instead of failing silently.
    // We still fall back to the cached token so callers don't break.
    // eslint-disable-next-line no-console
    console.warn('[antiforgery] failed to fetch token:', err)
    return latestXsrfToken
  }
}

const ensureXsrfHeaderToken = async () => {
  if (latestXsrfToken) return latestXsrfToken
  return await fetchAntiforgeryToken()
}

export const refreshAntiforgeryToken = async () => {
  clearAntiforgeryToken()
  return await fetchAntiforgeryToken()
}

const withXsrfConfig = (config: any = {}) => {
  const xsrfToken = latestXsrfToken
  const headers = { ...(config.headers || {}) }

  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = xsrfToken
  }

  return {
    ...config,
    withCredentials: true,
    headers
  }
}

axios.interceptors.request.use((config) => {
  const xsrfToken = latestXsrfToken

  if (xsrfToken) {
    config.headers = config.headers || {}
    config.headers['X-XSRF-TOKEN'] = xsrfToken
  }

  return config
})

axios.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const status = error?.response?.status
    const data = error?.response?.data
    const errorText =
      typeof data === 'string' ? data : data ? JSON.stringify(data) : ''
    const isAntiforgery =
      status === 400 &&
      errorText.toLowerCase().includes('antiforgery')

    if (isAntiforgery && error.config && !error.config._xsrfRetried) {
      error.config._xsrfRetried = true
      error.config.headers = error.config.headers || {}

      const retryToken = await fetchAntiforgeryToken()
      if (retryToken) {
        error.config.headers['X-XSRF-TOKEN'] = retryToken
      }

      return axios(error.config)
    }

    return Promise.reject(error)
  }
)

export async function callApiGet(path: string, config?: any): Promise<AxiosResponse> {
  return await axios.get(buildApiUrl(path), withXsrfConfig(config))
}

export async function callApiPost(
  path: string,
  payload: any,
  config: any = {}
): Promise<AxiosResponse> {
  await ensureXsrfHeaderToken()
  return await axios.post(buildApiUrl(path), payload, withXsrfConfig(config))
}


export async function callApiPostQueryParams(
  path: string,
  payload: any
): Promise<AxiosResponse> {
  await ensureXsrfHeaderToken()
  return await axios.post(buildApiUrl(path), null, withXsrfConfig({
    params: payload
  }))
}
